import * as XLSX from 'xlsx';
import { notification } from 'antd';

// Conversion factors to mm (multiply value in source unit to get mm)
const INCH_TO_MM = 25.4;
const CM_TO_MM = 10;
const M_TO_MM = 1000;
const FT_TO_MM = 304.8;

/**
 * Parses a length value from CSV/Excel (inch, ", mm, cm, m, ft) and returns value in mm only.
 * Used for PipeDiameter and WallThickness so they are always stored in mm.
 * @param {string} value - Raw value e.g. "6.625\"", "168.275 mm", "18.26 in"
 * @returns {string} - Numeric value in mm as string, or '' if invalid/empty
 */
export const parseLengthToMm = (value) => {
  if (value === null || value === undefined) return '';
  const str = typeof value === 'string' ? value.trim() : String(value).trim();
  if (str === '') return '';

  let numericStr = str
    .replace(/^[""]+|[""]+$/g, '') // Trim leading/trailing straight or curly quotes
    .trim();
  const lowerTrimmed = numericStr.toLowerCase();

  // Detect unit (order matters: check longer/more specific first)
  let factor = 1; // default: assume mm
  if (/\s*inches?\s*$/i.test(numericStr) || /\s*in\s*$/i.test(numericStr)) {
    factor = INCH_TO_MM;
    numericStr = numericStr.replace(/\s*inches?\s*$/i, '').replace(/\s*in\s*$/i, '').trim();
  } else if (/[""]\s*$/.test(str) || /\s*[""]\s*$/.test(numericStr)) {
    // Inch symbol " or " at end
    factor = INCH_TO_MM;
    numericStr = numericStr.replace(/\s*[""]\s*$/g, '').trim();
  } else if (/\s*mm\s*$/i.test(numericStr)) {
    factor = 1;
    numericStr = numericStr.replace(/\s*mm\s*$/i, '').trim();
  } else if (/\s*cm\s*$/i.test(numericStr)) {
    factor = CM_TO_MM;
    numericStr = numericStr.replace(/\s*cm\s*$/i, '').trim();
  } else if (/\s*m\s*$/i.test(lowerTrimmed) && !lowerTrimmed.endsWith('mm')) {
    factor = M_TO_MM;
    numericStr = numericStr.replace(/\s*m\s*$/i, '').trim();
  } else if (/\s*ft\s*$/i.test(numericStr) || /\s*feet\s*$/i.test(numericStr)) {
    factor = FT_TO_MM;
    numericStr = numericStr.replace(/\s*ft\s*$/i, '').replace(/\s*feet\s*$/i, '').trim();
  }

  const numMatch = numericStr.match(/-?\d+\.?\d*/);
  if (!numMatch) return '';
  const num = parseFloat(numMatch[0]);
  if (Number.isNaN(num)) return '';

  const mm = num * factor;
  // Round to 3 decimal places for storage
  return String(Math.round(mm * 1000) / 1000);
};

/**
 * Cleans numeric values by removing unit strings (mm, ", inches, in, cm, etc.)
 * Preserves decimal values and returns only the numeric part.
 * Use parseLengthToMm for PipeDiameter and WallThickness so they are stored in mm.
 * @param {string} value - The value to clean
 * @returns {string} - Cleaned numeric value as string
 */
const cleanNumericValue = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  // Trim whitespace
  let cleaned = value.trim();

  if (cleaned === '') {
    return '';
  }

  // Remove quotes from beginning and end (both straight " and curly ")
  cleaned = cleaned.replace(/^[""]+|[""]+$/g, '').trim();

  // Remove common unit strings (case-insensitive) with optional spaces
  cleaned = cleaned
    .replace(/\s*mm\s*$/i, '')
    .replace(/\s*[""]\s*$/g, '')
    .replace(/\s*inches?\s*$/i, '')
    .replace(/\s*in\s*$/i, '')
    .replace(/\s*cm\s*$/i, '')
    .replace(/\s*m\s*$/i, '')
    .replace(/\s*ft\s*$/i, '')
    .replace(/\s*feet\s*$/i, '')
    .trim();

  const numericMatch = cleaned.match(/^-?\d+\.?\d*$/);
  if (numericMatch) return numericMatch[0];
  const numberExtract = cleaned.match(/-?\d+\.?\d*/);
  if (numberExtract) return numberExtract[0];
  return '';
};

/**
 * Parses CSV or Excel file and extracts ID, joint_reference, pipe_diameter, and wall_thickness
 * @param {File} file - The CSV or Excel file to parse
 * @returns {Promise<{success: boolean, data: Array, errors: Array}>}
 */
export const parseCsvUpdateFile = async (file) => {
  try {
    // Validate file type
    const fileName = file.name.toLowerCase();
    const isValidFile =
      fileName.endsWith('.csv') ||
      fileName.endsWith('.xlsx') ||
      fileName.endsWith('.xls');

    if (!isValidFile) {
      return {
        success: false,
        data: [],
        errors: ['Invalid file type. Please upload a CSV or Excel file (.csv, .xlsx, .xls)'],
      };
    }

    // Read file as array buffer
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });

    // Get first sheet
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    // Convert to JSON with header row
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    if (!jsonData || jsonData.length < 2) {
      return {
        success: false,
        data: [],
        errors: ['File is empty or has no data rows'],
      };
    }

    // Get headers (first row)
    const headers = jsonData[0].map((h) => String(h || '').trim());

    // Find column indices (case-insensitive)
    const findColumnIndex = (columnNames) => {
      for (const name of columnNames) {
        const index = headers.findIndex(
          (h) => h.toLowerCase() === name.toLowerCase()
        );
        if (index !== -1) return index;
      }
      return -1;
    };

    const idIndex = findColumnIndex(['ID', 'id', 'Id']);
    const jointRefIndex = findColumnIndex([
      'joint_reference',
      'JointReference',
      'joint reference',
      'Joint Reference',
    ]);
    const pipeDiaIndex = findColumnIndex([
      'pipe_diameter',
      'PipeDiameter',
      'pipe diameter',
      'Pipe Diameter',
    ]);
    const wallThickIndex = findColumnIndex([
      'wall_thickness',
      'WallThickness',
      'wall thickness',
      'Wall Thickness',
    ]);

    // Validate ID column exists
    if (idIndex === -1) {
      return {
        success: false,
        data: [],
        errors: ['ID column not found in file. Please ensure your file has an ID column.'],
      };
    }

    const errors = [];
    const parsedData = [];

    // Process data rows (skip header row)
    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i];
      const id = row[idIndex];

      // Skip empty rows
      if (!id && !row.some((cell) => cell !== null && cell !== undefined && cell !== '')) {
        continue;
      }

      // ID is required
      if (!id || String(id).trim() === '') {
        errors.push(`Row ${i + 1}: Missing ID value`);
        continue;
      }

      // Extract raw values
      const rawPipeDiameter = pipeDiaIndex !== -1 && row[pipeDiaIndex] !== undefined && row[pipeDiaIndex] !== null
        ? String(row[pipeDiaIndex]).trim()
        : '';
      const rawWallThickness = wallThickIndex !== -1 && row[wallThickIndex] !== undefined && row[wallThickIndex] !== null
        ? String(row[wallThickIndex]).trim()
        : '';

      const record = {
        ID: String(id).trim(),
        JointReference: jointRefIndex !== -1 && row[jointRefIndex] !== undefined && row[jointRefIndex] !== null
          ? String(row[jointRefIndex]).trim()
          : '',
        PipeDiameter: parseLengthToMm(rawPipeDiameter),
        WallThickness: parseLengthToMm(rawWallThickness),
      };

      parsedData.push(record);
    }

    if (parsedData.length === 0) {
      return {
        success: false,
        data: [],
        errors: ['No valid data rows found in file'],
      };
    }

    // Show warnings for missing optional columns
    if (jointRefIndex === -1) {
      errors.push('Warning: joint_reference column not found');
    }
    if (pipeDiaIndex === -1) {
      errors.push('Warning: pipe_diameter column not found');
    }
    if (wallThickIndex === -1) {
      errors.push('Warning: wall_thickness column not found');
    }

    return {
      success: true,
      data: parsedData,
      errors: errors.length > 0 ? errors : [],
    };
  } catch (error) {
    console.error('Error parsing file:', error);
    return {
      success: false,
      data: [],
      errors: [`Error parsing file: ${error.message}`],
    };
  }
};
